/** Presentation helpers shared across screens. */

const CURRENCY = 'EGP'

const numberFormatter = new Intl.NumberFormat('en-GB')
const compactFormatter = new Intl.NumberFormat('en-GB', { notation: 'compact', maximumFractionDigits: 1 })
const currencyFormatter = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: CURRENCY,
  currencyDisplay: 'narrowSymbol',
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
})

export function formatNumber(value: number | null | undefined, fallback = '—'): string {
  return typeof value === 'number' && Number.isFinite(value) ? numberFormatter.format(value) : fallback
}

/** Keeps large KPI figures from overflowing their card. */
export function formatCompactNumber(value: number | null | undefined, fallback = '—'): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  return value >= 10_000 ? compactFormatter.format(value) : numberFormatter.format(value)
}

export function formatCurrency(value: number | null | undefined, fallback = '—'): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback
  try {
    return currencyFormatter.format(value)
  } catch {
    return `${CURRENCY} ${numberFormatter.format(value)}`
  }
}

/**
 * Formats an amount held in integer minor units (EGP piastres).
 *
 * The refund subsystem speaks minor units from the database to the Paymob
 * boundary, so this is the only place the browser turns one into a major-unit
 * figure — and it only does so for display. No arithmetic is done on the
 * converted value, which is what keeps a refund amount from drifting by a
 * piastre between the ledger and the screen.
 */
export function formatMinorCurrency(
  minorUnits: number | null | undefined,
  currency = CURRENCY,
  fallback = '—',
): string {
  if (typeof minorUnits !== 'number' || !Number.isFinite(minorUnits)) return fallback

  const major = minorUnits / 100
  try {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(major)
  } catch {
    return `${currency} ${major.toFixed(2)}`
  }
}

/** The plain decimal an operator types into an amount field: 25000 -> "250.00". */
export function minorToDecimalInput(minorUnits: number): string {
  return (minorUnits / 100).toFixed(2)
}

/**
 * Parses an operator-typed decimal back into minor units.
 *
 * Rounds rather than truncates, so "250.005" cannot silently become 25000 and
 * short-change the customer. Returns null for anything that is not a number.
 */
export function decimalInputToMinor(value: string): number | null {
  const trimmed = value.trim()
  if (trimmed === '' || !/^\d*\.?\d*$/.test(trimmed)) return null

  const parsed = Number(trimmed)
  if (!Number.isFinite(parsed)) return null
  return Math.round(parsed * 100)
}

/**
 * A span of seconds as an operator reads it: "45s", "12m", "3m 20s", "2h 15m",
 * "1d 3h". Seconds are kept only under ten minutes, where they still matter;
 * above that the largest two units carry the meaning and the rest is noise.
 *
 * Used for response times, which run from seconds to days. Null, non-finite or
 * negative input is the fallback — a wait cannot run backwards.
 */
export function formatDuration(seconds: number | null | undefined, fallback = '—'): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0) return fallback

  const whole = Math.round(seconds)
  if (whole < 60) return `${whole}s`

  const totalMinutes = Math.floor(whole / 60)
  if (totalMinutes < 60) {
    const remainderSeconds = whole % 60
    return remainderSeconds > 0 && totalMinutes < 10
      ? `${totalMinutes}m ${remainderSeconds}s`
      : `${totalMinutes}m`
  }

  const totalHours = Math.floor(totalMinutes / 60)
  if (totalHours < 24) {
    const remainderMinutes = totalMinutes % 60
    return remainderMinutes > 0 ? `${totalHours}h ${remainderMinutes}m` : `${totalHours}h`
  }

  const days = Math.floor(totalHours / 24)
  const remainderHours = totalHours % 24
  return remainderHours > 0 ? `${days}d ${remainderHours}h` : `${days}d`
}

/** "ON_THE_WAY" -> "On the way". */
export function humanizeEnum(value: string | null | undefined, fallback = '—'): string {
  if (!value) return fallback
  const words = value.replace(/_/g, ' ').toLowerCase().trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function formatPercentage(value: number | null | undefined, fallback = '—'): string {
  return typeof value === 'number' && Number.isFinite(value) ? `${numberFormatter.format(value)}%` : fallback
}

/**
 * Renders a promo discount the way an operator reads it: "20% (max EGP 150)"
 * or "EGP 50 off".
 */
export function formatDiscount(
  discountType: string | null | undefined,
  discountValue: number | null | undefined,
  maxDiscountAmount: number | null | undefined,
): string {
  if (typeof discountValue !== 'number') return '—'

  if (discountType?.toUpperCase() === 'PERCENTAGE') {
    const cap = typeof maxDiscountAmount === 'number' && maxDiscountAmount > 0
      ? ` (max ${formatCurrency(maxDiscountAmount)})`
      : ''
    return `${formatPercentage(discountValue)}${cap}`
  }
  return `${formatCurrency(discountValue)} off`
}

/** "0 of 100 used" — or "unlimited", which is what a total limit of 0 means. */
export function formatUsage(used: number | null | undefined, total: number | null | undefined): string {
  const usedCount = typeof used === 'number' ? used : 0
  if (typeof total !== 'number' || total <= 0) {
    return `${formatNumber(usedCount)} used · no cap`
  }
  return `${formatNumber(usedCount)} of ${formatNumber(total)}`
}

export function initialsOf(name: string | null | undefined): string {
  if (!name) return '—'
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '—'
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase()
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase()
}

/** Trims an address for a table cell without cutting mid-word where avoidable. */
export function truncate(value: string | null | undefined, max = 48, fallback = '—'): string {
  if (!value) return fallback
  const trimmed = value.trim()
  if (trimmed.length <= max) return trimmed

  const cut = trimmed.slice(0, max)
  const lastSpace = cut.lastIndexOf(' ')
  return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
